import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { coverUrl } from "@/lib/api";
import { useI18n } from "@/context/I18n";
import { Loader2, ListMusic } from "lucide-react";

/**
 * Uploads whose artist no metadata provider could confirm, grouped by the
 * channel that posted them. They are playlists put together by an uploader,
 * not artists, so they are kept out of the Artists page entirely.
 */
export default function Collections() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [shared, setShared] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api
      .get("/music/collections")
      .then((r) => {
        setData(r.data.collections);
        setShared(r.data.playlists ?? []);
      })
      .catch(() => setData([]));
  }, []);

  if (data === null) {
    return (
      <div className="p-10 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  const term = q.trim().toLowerCase();
  const shown = term ? data.filter((c) => c.name.toLowerCase().includes(term)) : data;

  return (
    <div className="p-4 md:p-8 pb-6" data-testid="collections-page">
      <h1 className="font-display text-3xl font-black tracking-tighter">{t("collections")}</h1>
      <p className="text-zinc-400 text-sm mt-2 max-w-2xl">{t("collections_sub")}</p>

      <input
        data-testid="collections-filter"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t("search_library_ph")}
        className="mt-5 mb-6 w-full max-w-sm bg-white/5 rounded-lg px-3 py-2 text-sm placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF2A4D]"
      />

      {/* What listeners chose to share, kept apart from the channel groupings
          below: one is somebody's selection, the other is whatever an
          uploader happened to post. */}
      {shared.length > 0 && (
        <section className="mb-10" data-testid="shared-playlists">
          <h2 className="font-heading text-xl font-bold mb-3">{t("shared_playlists")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
            {shared
              .filter((p) => !term || p.name.toLowerCase().includes(term))
              .map((p) => (
                <Link
                  key={p.id}
                  to={`/app/playlist/${p.id}`}
                  data-testid={`shared-playlist-${p.id}`}
                  className="bg-white/5 hover:bg-white/10 rounded-xl p-3 transition-colors"
                >
                  {p.cover ? (
                    <img src={coverUrl(p.cover, 250)} alt="" className="w-full aspect-square rounded-lg object-cover" />
                  ) : (
                    <div className="w-full aspect-square rounded-lg bg-gradient-to-br from-[#FF2A4D]/60 to-purple-700/50 flex items-center justify-center">
                      <ListMusic className="w-8 h-8 text-white/70" />
                    </div>
                  )}
                  <p className="text-sm font-semibold truncate mt-2">{p.name}</p>
                  <p className="text-xs text-zinc-400 truncate">
                    {p.owner_name ? `${p.owner_name} · ` : ""}{p.track_count} {t("tracks")}
                  </p>
                </Link>
              ))}
          </div>
        </section>
      )}

      {shown.length === 0 ? (
        <p className="text-zinc-500 text-sm">{t("no_results")}</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
          {shown.map((c) => (
            <Link
              key={c.name}
              to={`/app/collection/${encodeURIComponent(c.name)}`}
              data-testid={`collection-${c.name}`}
              className="bg-white/5 hover:bg-white/10 rounded-xl p-3 transition-colors"
            >
              {c.cover ? (
                <img src={coverUrl(c.cover, 250)} alt="" className="w-full aspect-square rounded-lg object-cover" />
              ) : (
                <div className="w-full aspect-square rounded-lg bg-gradient-to-br from-zinc-700 to-zinc-900 flex items-center justify-center">
                  <ListMusic className="w-8 h-8 text-zinc-500" />
                </div>
              )}
              <p className="text-sm font-semibold truncate mt-2">{c.name}</p>
              <p className="text-xs text-zinc-400">
                {c.track_count} {t("tracks")}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
